/**
 * Memories introduction modal — shown after 5 runs to drive adoption of the
 * memories feature and collect Role/Company/Country data. Users can optionally add
 * their first memory and update profile fields, or simply dismiss.
 */

import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useId, useState } from "react";
import { toast } from "sonner";
import { create } from "zustand";
import { CountrySelector } from "@/components/country-selector";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authClient } from "@/lib/api/auth-client";
import { memoriesFetch } from "@/lib/api/memories";
import { useAuthStore } from "@/stores/auth-store";

// --- Store ---

interface MemoriesIntroState {
	isOpen: boolean;
	open: () => void;
	close: () => void;
}

export const useMemoriesIntroStore = create<MemoriesIntroState>((set) => ({
	isOpen: false,
	open: () => set({ isOpen: true }),
	close: () => set({ isOpen: false }),
}));

// --- Types ---

interface MemoryItem {
	id: number;
	content: string;
}

// --- Modal ---

export function MemoriesIntroModal() {
	const { isOpen, close } = useMemoriesIntroStore();
	const queryClient = useQueryClient();
	const user = useAuthStore((s) => s.user);

	const [jobTitle, setJobTitle] = useState("");
	const [company, setCompany] = useState("");
	const [country, setCountry] = useState("");
	const [memory, setMemory] = useState("");
	const [saving, setSaving] = useState(false);
	const [existingMemories, setExistingMemories] = useState<MemoryItem[]>([]);

	const roleId = useId();
	const companyId = useId();
	const memoryId = useId();

	// Sync form state and fetch existing memories when modal opens
	useEffect(() => {
		if (isOpen && user) {
			setJobTitle(user.job_title || "");
			setCompany(user.company || "");
			setCountry(user.country || "");
		}
		if (isOpen) {
			memoriesFetch("")
				.then((data) => setExistingMemories(data || []))
				.catch((err: unknown) => console.error("[Memories] Failed to fetch:", err));
		}
	}, [isOpen, user]);

	/** Persist dismiss flag to backend and update auth store directly. */
	const dismiss = async () => {
		try {
			await authClient.PATCH("/api/v1/auth/me", {
				body: { memories_intro_dismissed: true },
			});
			const current = useAuthStore.getState().user;
			if (current) {
				useAuthStore.getState().setUser({ ...current, memories_intro_dismissed: true });
			}
			queryClient.invalidateQueries({ queryKey: ["current-user"] });
			close();
		} catch {
			toast.error("Failed to dismiss. Please try again.");
		}
	};

	const handleSave = async () => {
		setSaving(true);
		try {
			// Update profile (role, company, country, dismiss flag) in one call
			const profileUpdate: Record<string, unknown> = { memories_intro_dismissed: true };
			if (jobTitle.trim()) profileUpdate.job_title = jobTitle.trim();
			if (company.trim()) profileUpdate.company = company.trim();
			if (country.trim()) profileUpdate.country = country.trim();

			await authClient.PATCH("/api/v1/auth/me", { body: profileUpdate });

			// Create memory if provided
			if (memory.trim()) {
				await memoriesFetch("", {
					method: "POST",
					body: JSON.stringify({ content: memory.trim() }),
				});
				queryClient.invalidateQueries({ queryKey: ["user-memories"] });
			}

			const current = useAuthStore.getState().user;
			if (current) {
				useAuthStore.getState().setUser({ ...current, memories_intro_dismissed: true });
			}
			queryClient.invalidateQueries({ queryKey: ["current-user"] });
			toast.success("Saved successfully");
			close();
		} catch {
			toast.error("Failed to save. Please try again.");
		} finally {
			setSaving(false);
		}
	};

	const memoryCount = existingMemories.length;
	const remainingCount = memoryCount - 1;

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && close()}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>Help your mates know you better</DialogTitle>
					<DialogDescription>
						Improve and personalize responses by sharing a bit about yourself. Your mates remember
						what you tell them across conversations, so they get better over time.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
						<div className="space-y-2">
							<Label htmlFor={roleId}>Role</Label>
							<Input
								id={roleId}
								autoFocus
								value={jobTitle}
								onChange={(e) => setJobTitle(e.target.value)}
								placeholder="Your role"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor={companyId}>Company</Label>
							<Input
								id={companyId}
								value={company}
								onChange={(e) => setCompany(e.target.value)}
								placeholder="Your company"
							/>
						</div>
					</div>

					<CountrySelector value={country} onChange={setCountry} />

					<div className="space-y-2">
						<Label htmlFor={memoryId}>First memory (optional)</Label>
						<Input
							id={memoryId}
							value={memory}
							onChange={(e) => setMemory(e.target.value)}
							placeholder="e.g. I prefer concise bullet-point summaries"
							maxLength={300}
						/>
					</div>

					{memoryCount > 0 && (
						<div className="bg-muted/50 rounded-md px-3 py-2 text-sm">
							<p className="text-muted-foreground font-medium">
								{memoryCount} {memoryCount === 1 ? "memory" : "memories"} saved
							</p>
							<p className="mt-1 truncate">{existingMemories[0].content}</p>
							{remainingCount > 0 && (
								<Link
									to="/account"
									className="text-primary mt-1 inline-block text-xs hover:underline"
									onClick={close}
								>
									+{remainingCount} more
								</Link>
							)}
						</div>
					)}

					<p className="text-muted-foreground text-xs">
						You can always add more memories in{" "}
						<Link to="/account" className="text-primary hover:underline" onClick={close}>
							Account Settings
						</Link>
						.
					</p>
				</div>

				<DialogFooter className="gap-2 sm:gap-0">
					<Button variant="ghost" onClick={dismiss} disabled={saving}>
						Skip
					</Button>
					<Button onClick={handleSave} disabled={saving}>
						{saving ? "Saving..." : "Save & Continue"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
